import { onUnmounted } from 'vue';

/**
 * Composable for managing SEO meta tags, OpenGraph, Twitter Cards, and Schema.org JSON-LD
 */
export function useSeo() {
  const createdElements = [];

  const setMetaTag = (attributeName, attributeValue, content) => {
    if (!content) return;
    let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
    let isNew = false;

    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attributeName, attributeValue);
      document.head.appendChild(element);
      isNew = true;
    }

    element.setAttribute('content', content);
    if (isNew) {
      createdElements.push(element);
    }
  };

  const setCanonical = (url) => {
    if (!url) return;
    let link = document.querySelector('link[rel="canonical"]');
    let isNew = false;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
      isNew = true;
    }

    link.setAttribute('href', url);
    if (isNew) {
      createdElements.push(link);
    }
  };

  const setJsonLd = (schemaData, scriptId = 'schema-jsonld') => {
    if (!schemaData) return;
    let script = document.getElementById(scriptId);
    let isNew = false;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
      isNew = true;
    }

    script.textContent = JSON.stringify(schemaData, null, 2);
    if (isNew) {
      createdElements.push(script);
    }
  };

  const updateSeo = ({
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = 'website',
    ogUrl,
    twitterCard = 'summary_large_image',
    price,
    currency = 'DZD',
    jsonLd,
    jsonLdId = 'page-schema-jsonld'
  }) => {
    if (title) {
      document.title = title;
    }

    if (description) {
      setMetaTag('name', 'description', description);
    }

    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // Canonical
    const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : '');
    if (currentUrl) {
      setCanonical(currentUrl);
    }

    // OpenGraph
    setMetaTag('property', 'og:title', ogTitle || title);
    setMetaTag('property', 'og:description', ogDescription || description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:url', ogUrl || currentUrl);
    if (ogImage) {
      setMetaTag('property', 'og:image', ogImage);
    }

    // Product specific OpenGraph
    if (price) {
      setMetaTag('property', 'product:price:amount', price.toString());
      setMetaTag('property', 'product:price:currency', currency);
    }

    // Twitter Card
    setMetaTag('name', 'twitter:card', twitterCard);
    setMetaTag('name', 'twitter:title', ogTitle || title);
    setMetaTag('name', 'twitter:description', ogDescription || description);
    if (ogImage) {
      setMetaTag('name', 'twitter:image', ogImage);
    }

    // JSON-LD Structured Data
    if (jsonLd) {
      setJsonLd(jsonLd, jsonLdId);
    }
  };

  const cleanupSeo = () => {
    createdElements.forEach(el => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    createdElements.length = 0;
  };

  onUnmounted(() => {
    cleanupSeo();
  });

  return {
    updateSeo,
    cleanupSeo,
    setJsonLd,
    setCanonical
  };
}
